import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './CartItems.css';
import { ShopContext } from '../../Context/ShopContext';
import { getOptimizedImageUrl, setOptimizedImageFallback } from '../../utils/imageOptimization';
import { 
  FaShoppingCart, 
  FaTrashAlt, 
  FaShoppingBag, 
  FaTruck,
  FaTag,
  FaChevronRight,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle
} from 'react-icons/fa';

const CartItems = () => {
  const navigate = useNavigate();
  const { 
    getTotalCartAmount, 
    removeFromCart,
    getCartItemsDetails
  } = useContext(ShopContext);
  
  const [promoCode, setPromoCode] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  
  const cartItemsDetails = getCartItemsDetails();

  // Define valid promo codes
  const validPromoCodes = [
    { code: 'WELCOME10', discount: 10, type: 'percentage' },
    { code: 'SAVE20', discount: 20, type: 'percentage' },
    { code: 'FREESHIP', discount: 0, type: 'free_shipping' },
    { code: 'NEPLISHOP25', discount: 25, type: 'percentage' },
    { code: 'SUMMER15', discount: 15, type: 'percentage' }
  ];

  // Function to validate promo code
  const validatePromoCode = (code) => {
    const validCode = validPromoCodes.find(promo => 
      promo.code.toLowerCase() === code.toLowerCase()
    );
    return validCode || null;
  };

  // Handle promo code apply
  const handleApplyPromoCode = () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code!', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
        icon: <FaExclamationTriangle />
      });
      return;
    }

    setIsApplying(true);
    
    // Simulate API call delay
    setTimeout(() => {
      const validPromo = validatePromoCode(promoCode);
      
      if (validPromo) {
        toast.success(`🎉 Promo code "${validPromo.code}" applied successfully! ${validPromo.type === 'percentage' ? `You got ${validPromo.discount}% off!` : 'Free shipping activated!'}`, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          icon: <FaCheckCircle />
        });
        
        // Here you would typically update the cart total with discount
        setPromoCode('');
      } else {
        toast.error(`❌ Invalid promo code "${promoCode}". Please try another code.`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          icon: <FaTimesCircle />
        });
      }
      
      setIsApplying(false);
    }, 800);
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyPromoCode();
    }
  };

  if (cartItemsDetails.length === 0) {
    return (
      <div className="cartitems-empty">
        <div className="empty-cart-icon">
          <FaShoppingCart />
        </div>
        <h2>Your cart is empty</h2>
        <p>Looks like you haven't added anything to your cart yet.</p>
        <button 
          onClick={() => navigate('/')} 
          className="continue-shopping-btn"
        >
          <FaShoppingBag /> Continue Shopping
        </button>
      </div>
    );
  }

  return (
    <div className='cartitems'>
      {/* Toast Container */}
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        style={{ marginTop: '60px' }}
      />
      
      {/* Cart Header */}
      <div className="cart-header">
        <h1>
          <FaShoppingCart /> Your Shopping Cart
        </h1>
        <div className="cart-summary-badge">
          {cartItemsDetails.length} {cartItemsDetails.length === 1 ? 'item' : 'items'}
        </div>
      </div>
      
      {/* Cart Items Container */}
      <div className="cart-items-container">
        {/* Desktop Header */}
        <div className="cartitems-format-main">
          <p className="col-product">Product</p>
          <p className="col-title">Details</p>
          <p className="col-size">Size</p>
          <p className="col-price">Price</p>
          <p className="col-quantity">Quantity</p>
          <p className="col-total">Total</p>
          <p className="col-remove">Remove</p>
        </div>
        <hr className="header-divider" />
        
        {/* Cart Items */}
        {cartItemsDetails.map((item) => {
          const { product, quantity, size, cartKey } = item;
          
          return (
            <React.Fragment key={cartKey}>
              <div className='cartitems-format'>
                {/* Product Image */}
                <div className="cartitem-image-container">
                  <img 
                    src={getOptimizedImageUrl(product.main_image, { width: 160, quality: 70 })} 
                    className='carticon-product-icon' 
                    alt={product.name}
                    loading="lazy"
                    decoding="async"
                    fetchpriority="low"
                    width="80"
                    height="80"
                    onError={(e) => {
                      setOptimizedImageFallback(e, product.main_image, 'https://via.placeholder.com/80x80?text=Product');
                    }}
                  />
                </div>
                
                {/* Product Details */}
                <div className="cartitem-details">
                  <h4 className="cartitem-title">{product.name}</h4>
                  <div className="cartitem-attributes">
                    {product.color && (
                      <span className="cartitem-attribute">
                        <span className="attribute-label">Color:</span> 
                        <span className="attribute-value">{product.color}</span>
                      </span>
                    )}
                    {product.category && (
                      <span className="cartitem-attribute">
                        <span className="attribute-label">Category:</span> 
                        <span className="attribute-value">{product.category}</span>
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Size */}
                <div className="cartitem-size-cell">
                  <span className="cartitem-size">{size || 'One Size'}</span>
                </div>
                
                {/* Price */}
                <div className="cartitem-price-cell">
                  <span className="cartitem-price">Rs {product.new_price.toLocaleString()}</span>
                </div>
                
                {/* Quantity */}
                <div className="cartitem-quantity-cell">
                  <div className="cartitems-quantity">
                    <span className="quantity-value">{quantity}</span>
                  </div>
                </div>
                
                {/* Total */}
                <div className="cartitem-total-cell">
                  <span className="cartitem-total">Rs {(product.new_price * quantity).toLocaleString()}</span>
                </div>
                
                {/* Remove Button */}
                <div className="cartitem-remove-cell">
                  <button 
                    onClick={() => removeFromCart(product.id, size)} 
                    className="cartitems-remove-btn"
                    aria-label="Remove item"
                  >
                    <FaTrashAlt className="remove-icon" />
                  </button>
                </div>
                
                {/* Mobile Layout */}
                <div className="cartitems-details-mobile">
                  <div className="cartitem-mobile-row">
                    <span className="mobile-label">Size:</span>
                    <span className="mobile-value">{size || 'One Size'}</span>
                  </div>
                  <div className="cartitem-mobile-row">
                    <span className="mobile-label">Price:</span>
                    <span className="mobile-value">Rs {product.new_price.toLocaleString()}</span>
                  </div>
                  <div className="cartitem-mobile-row">
                    <span className="mobile-label">Total:</span>
                    <span className="mobile-value">Rs {(product.new_price * quantity).toLocaleString()}</span>
                  </div>
                  <button 
                    onClick={() => removeFromCart(product.id, size)} 
                    className="mobile-remove-btn"
                  >
                    <FaTrashAlt /> Remove
                  </button>
                </div>
              </div>
              <hr className="item-divider" />
            </React.Fragment>
          );
        })}
      </div>
      
      {/* Cart Summary */}
      <div className="cartitems-down">
        <div className="cartitems-total">
          <div className="total-header">
            <h2>
              <FaCheckCircle /> Cart Summary
            </h2>
            <div className="total-items-count">
              {cartItemsDetails.length} {cartItemsDetails.length === 1 ? 'item' : 'items'}
            </div>
          </div>
          
          <div className="cartitems-total-details">
            <div className="cartitems-total-item">
              <div className="total-item-label">
                <span>Subtotal</span>
              </div>
              <div className="total-item-value">
                <span>Rs {getTotalCartAmount().toLocaleString()}</span>
              </div>
            </div>
            
            <div className="cartitems-total-item">
              <div className="total-item-label">
                <FaTruck className="total-icon" />
                <span>Delivery Fee</span>
              </div>
              <div className="total-item-value free-delivery">
                <span>Free</span>
              </div>
            </div>
            
            {/* Promo Code Discount (will appear when valid code is applied) */}
            <div className="cartitems-total-item promo-discount" style={{ display: 'none' }}>
              <div className="total-item-label">
                <FaTag className="total-icon" />
                <span>Promo Discount</span>
              </div>
              <div className="total-item-value discount-value">
                <span>- Rs 0</span>
              </div>
            </div>
            
            <hr className="total-divider" />
            
            <div className="cartitems-total-item final-total">
              <div className="total-item-label">
                <h3>Total Amount</h3>
              </div>
              <div className="total-item-value">
                <h3 className="total-amount">Rs {getTotalCartAmount().toLocaleString()}</h3>
              </div>
            </div>
          </div>
          
          <button 
            onClick={() => navigate('/checkout')} 
            className="checkout-btn"
          >
            Proceed to Checkout <FaChevronRight className="btn-arrow" />
          </button>
          
          <div className="secure-checkout-note">
            <FaCheckCircle className="secure-icon" />
            <span>Secure checkout • SSL encrypted</span>
          </div>
        </div>
        
        <div className="cartitems-promocode">
          <div className="promo-header">
            <FaTag className="promo-icon" />
            <h3>Have a Promo Code?</h3>
          </div>
          <p>Enter your promo code to get special discounts</p>
          
          <div className="cartitems-promobox">
            <input 
              type="text" 
              placeholder='Enter promo code' 
              className="promo-input"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isApplying}
            />
            <button 
              className={`promo-submit-btn ${isApplying ? 'applying' : ''}`}
              onClick={handleApplyPromoCode}
              disabled={isApplying}
            >
              {isApplying ? (
                <>
                  <span className="spinner"></span> Applying...
                </>
              ) : (
                'Apply Code'
              )}
            </button>
          </div>
        
          
          <div className="promo-terms">
            <small>Promo codes are subject to terms & conditions</small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItems;
